/**
 * Breadcrumb — informational navigation path showing current route location.
 *
 * Format: "Home > Group Name > Route Label"
 * or just "Home > Route Label" for ungrouped routes.
 *
 * No navigation links — breadcrumb is read-only. Atlas navigation is
 * entirely sidebar-driven.
 *
 * doc 147 §5.2 (Phase 1 UI).
 */

import type { Route } from '../state/hashRouter.js';
import { getGroupForRoute, getNavGroup } from '../state/navGroups.js';
import { getRouteLabel } from '../state/routeLabels.js';

export interface BreadcrumbProps {
  route: Route;
  /** Optional: custom styling class */
  className?: string;
}

export function Breadcrumb({ route, className }: BreadcrumbProps): React.JSX.Element {
  const group = getGroupForRoute(route.kind);
  const groupConfig = group ? getNavGroup(group) : null;
  const routeLabel = getRouteLabel(route);

  const parts: Array<{ text: string; isCurrent: boolean }> = [
    { text: 'Home', isCurrent: false },
  ];

  if (groupConfig) {
    parts.push({ text: groupConfig.label, isCurrent: false });
  }

  parts.push({ text: routeLabel, isCurrent: true });

  return (
    <nav className={`breadcrumb ${className ?? ''}`} aria-label="Breadcrumb">
      <ol className="breadcrumb-list">
        {parts.map((part, i) => (
          <li key={i} className="breadcrumb-item">
            <span
              className={`breadcrumb-text ${part.isCurrent ? 'breadcrumb-text--current' : 'breadcrumb-text--prefix'}`}
            >
              {part.text}
            </span>
            {i < parts.length - 1 && <span className="breadcrumb-separator"> &gt; </span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}
